import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { OrdemServico } from '../types/ordemServico';
import { api } from '../services/api';
import { buildEtiquetaDataFromOS, imprimirEtiquetasOS } from '../utils/zebraEtiqueta';

interface EtiquetasModalProps {
  os: OrdemServico;
  onClose: () => void;
}

const EtiquetasModal: React.FC<EtiquetasModalProps> = ({ os, onClose }) => {
  const [printing, setPrinting] = useState<1 | 5 | null>(null);

  const preview = useMemo(() => buildEtiquetaDataFromOS(os), [os]);

  const handlePrint = async (quantidade: 1 | 5) => {
    try {
      setPrinting(quantidade);

      let osParaImpressao = os;
      if (os.id) {
        try {
          const fasesResponse = await api.get(`/os/${os.id}/fases`);
          osParaImpressao = { ...os, fases: fasesResponse.data.fases || os.fases || [] };
        } catch {
          // Mantém fases já carregadas na lista
        }
      }

      await imprimirEtiquetasOS(osParaImpressao, quantidade);
      toast.success(
        quantidade === 1
          ? '1 etiqueta enviada para a impressora'
          : '5 etiquetas enviadas para a impressora'
      );
      onClose();
    } catch (error: any) {
      console.error('Erro ao imprimir etiquetas:', error);
      toast.error(error?.message || 'Erro ao imprimir etiquetas');
    } finally {
      setPrinting(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Etiquetas</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-gray-600">
            Impressão direta na Zebra (Browser Print). Escolha a quantidade.
          </p>

          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800 space-y-1">
            <div><span className="font-medium">Nº Nota:</span> {preview.numeroNota || '-'}</div>
            <div><span className="font-medium">Nome:</span> {preview.nome || '-'}</div>
            <div><span className="font-medium">Remoção:</span> {preview.remocao}</div>
            <div><span className="font-medium">Clínica:</span> {preview.clinica}</div>
            <div><span className="font-medium">Velório:</span> {preview.velorio}</div>
            <div><span className="font-medium">Cemitério:</span> {preview.cemiterio}</div>
          </div>

          {!preview.numeroNota && (
            <p className="text-sm text-red-600">
              Esta OS não possui Número da Nota de Contratação.
            </p>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={printing !== null}
            className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => handlePrint(1)}
            disabled={printing !== null || !preview.numeroNota}
            className="px-4 py-2 rounded-lg bg-white border border-blue-600 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
          >
            {printing === 1 ? 'Imprimindo...' : 'Imprimir 1'}
          </button>
          <button
            type="button"
            onClick={() => handlePrint(5)}
            disabled={printing !== null || !preview.numeroNota}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {printing === 5 ? 'Imprimindo...' : 'Imprimir 5'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EtiquetasModal;
